<template>
  <div id="position-form">
    <template v-if="!editing">
      <div class="my-0 py-0">
        <div class="d-flex justify-space-between">
          <h1 class="section-title my-0 card-title">
            {{ $t(`views.orders.orderModal.createNewPosition.title`) }}
          </h1>

          <div class="position-form-select-data-from-watch">
            <v-menu
              v-model="getDataFromWatchActive"
              :close-on-content-click="false"
              transition="scale-transition"
              left
              offset-y
              min-width="400px"
              class="position-form-select-data-from-watch-menu"
              content-class="elevation-1"
            >
              <template v-slot:activator="{ on, attrs }">
                <a class="primary--text" v-bind="attrs" v-on="on">
                  <v-tooltip top max-width="300px">
                    <template v-slot:activator="{ onTooltip, attrsTooltip }">
                      <v-icon v-bind="attrsTooltip" v-on="onTooltip">mdi-bullseye-arrow</v-icon>
                    </template>
                    <span>
                      {{ $t(`views.orders.orderModal.createNewPosition.getDataFromWatch`) }}
                    </span>
                  </v-tooltip>
                </a>
              </template>
              <v-card class="position-form-select-data-from-watch-menu-card pt-3 pb-4 px-4 my-0">
                <PositionSearch
                  v-if="getDataFromWatchActive"
                  ref="watchSearch"
                  :disabled="loading"
                  :type="positionPositionTypes"
                  searchType="watch"
                  @input="getDataFromWatch"
                />
              </v-card>
            </v-menu>
          </div>
        </div>

        <div class="mt-3">
          <v-chip-group v-model="myPosition.type" active-class="primary" mandatory show-arrows>
            <v-chip
              v-for="positionTypeElem in positionTypes"
              :key="positionTypeElem.value"
              :value="positionTypeElem.value"
              outlined
            >
              {{ positionTypeElem.text }}
            </v-chip>
          </v-chip-group>
        </div>
      </div>
    </template>

    <template v-if="myPosition && myPosition.type">
      <v-row>
        <v-col cols="12" md="12" class="my-0 py-0">
          <template v-if="editing">
            <h1>
              {{ myPosition.type ? $t(`views.stocks.stockModal.title.${myPosition.type}`) : '' }}
            </h1>
            <!-- <div class="mt-1 mb-0">
            <a
              v-if="editing && myPosition.type === 'fund'"
              class="position-form-link"
              @click="$emit('changePositionToType', 'etf')"
            >
              {{ $t('views.stocks.stockModal.changeToETF') }}
            </a>
          </div>
          <div>
            <a
              v-if="editing && myPosition.type === 'fund'"
              class="position-form-link"
              @click="$emit('changePositionToType', 'plan')"
            >
              {{ $t('views.stocks.stockModal.changeToPlan') }}
            </a>
          </div> -->
          </template>
        </v-col>

        <template v-if="isDerivative">
          <v-col cols="12" md="12">
            <p v-if="!editing" class="pa-0 mx-0 mt-2 mb-1">
              {{
                $t(`views.orders.orderModal.createNewPosition.derivativeInfo`) +
                $t(`views.orders.orderModal.createNewPosition.type.${myPosition.type}`) +
                $t(`views.orders.orderModal.createNewPosition.derivativeInfo2`)
              }}
            </p>
            <v-chip-group
              v-model="myPosition.derivativePositionType"
              active-class="primary"
              mandatory
              show-arrows
            >
              <v-chip
                v-for="derivativePositionTypeElem in derivativeOptionsPositionTypes"
                :key="derivativePositionTypeElem.value"
                :value="derivativePositionTypeElem.value"
                outlined
                :disabled="editing"
              >
                {{ derivativePositionTypeElem.text }}
              </v-chip>
            </v-chip-group>
          </v-col>
        </template>

        <template
          v-if="
            [
              'stock',
              'fund',
              'right',
              'etf',
              'putOption',
              'callOption',
              'plan',
              'bond',
              'index',
              'custom',
            ].indexOf(positionOrDerivativePositionType) > -1
          "
        >
          <v-col cols="12" :md="showCountrySelector ? 4 : 9">
            <div class="items-flex">
              <v-text-field
                v-model="myPosition.ticker"
                :label="tickerLabelTranslation"
                :disabled="loading"
                maxLength="30"
                hide-details
              />
              <span class="ml-2">
                <InfoIcon :size="15" :text="tickerLabelHelp" />
              </span>
            </div>
          </v-col>
          <v-col v-if="showCountrySelector" cols="12" md="5">
            <div class="items-flex">
              <CountrySelector
                v-model="myPosition.country"
                :countries="countrySelectorList"
                :label="$t('views.stocks.stockModal.country')"
                translate-name
                :disabled="loading"
                hide-details
              />
              <span class="ml-2">
                <InfoIcon
                  :size="15"
                  :text="
                    $t(`views.stocks.stockModal.countryHelp.${positionOrDerivativePositionType}`)
                  "
                />
              </span>
            </div>
          </v-col>
          <v-col cols="12" md="3">
            <CurrencySelector
              v-model="myPosition.currency"
              :disabled="loading || editing"
              model="position"
              :label="$t('views.stocks.stockModal.currency')"
              :currencyFilter="positionCurrenciesFilter"
            />
          </v-col>
        </template>
        <template v-if="positionOrDerivativePositionType === 'crypto'">
          <v-col cols="12" md="12">
            <div class="items-flex">
              <v-text-field
                v-model="myPosition.ticker"
                :label="tickerLabelTranslation"
                :disabled="loading"
                maxLength="30"
                hide-details
              />
              <span class="ml-2">
                <InfoIcon :size="15" :text="tickerLabelHelp" />
              </span>
            </div>
          </v-col>
        </template>
        <template v-if="positionOrDerivativePositionType === 'commodity'">
          <v-col cols="12" md="12">
            <v-autocomplete
              v-model="myPosition.ticker"
              :items="commodityPositionTickers"
              :disabled="loading || editing"
              :label="$t('views.stocks.stockModal.commodityPosition.commodityPosition')"
              :placeholder="$t('views.stocks.stockModal.commodityPosition.commodityPosition')"
              hide-no-data
              hide-details
            ></v-autocomplete>
          </v-col>
        </template>
        <template v-if="positionOrDerivativePositionType === 'cash'">
          <v-col cols="12" md="12">
            <v-autocomplete
              v-model="myPosition.ticker"
              :items="cashPositionTickers"
              :disabled="loading || editing"
              :label="$t('views.stocks.stockModal.cashPosition.currency')"
              :placeholder="$t('views.stocks.stockModal.cashPosition.currency')"
              hide-no-data
              hide-details
            ></v-autocomplete>
          </v-col>
        </template>

        <v-col
          v-if="showExchangeSelector && exchangeSelectorList && exchangeSelectorList.length"
          cols="12"
          md="12"
        >
          <div class="items-flex">
            <v-select
              v-model="myPosition.exchange"
              :items="exchangeSelectorList"
              :label="$t('views.stocks.stockModal.exchange.label')"
              :disabled="loading"
              hide-details
            ></v-select>
            <span class="ml-2">
              <InfoIcon :size="15" :text="$t('views.stocks.stockModal.exchange.help')" />
            </span>
          </div>
        </v-col>

        <template v-if="positionOrDerivativePositionType === 'custom'">
          <v-col cols="12" md="8">
            <CustomTypeBox v-model="myPosition.customType" :disabled="loading" />
          </v-col>
          <v-col v-show="myPosition.currency" cols="12" md="4">
            <CurrencyInput
              v-model="myPosition.customMarket.eod.close"
              :currency="myPosition.currency"
              :label="$t('views.stocks.stockModal.customMarketEodClose.label')"
              :disabled="loading"
              outlined
            />
          </v-col>
        </template>

        <template>
          <v-col cols="12" md="12" class="pb-0 pt-3">
            <a class="position-form-link" @click="moreConfiguration = !moreConfiguration">
              {{
                !moreConfiguration
                  ? $t('views.stocks.stockModal.moreConfiguration')
                  : $t('views.stocks.stockModal.lessConfiguration')
              }}
              <v-icon color="primary">
                {{ !moreConfiguration ? 'mdi-chevron-down' : 'mdi-chevron-up' }}
              </v-icon>
            </a>
          </v-col>

          <template v-if="moreConfiguration">
            <v-col
              v-if="['fund', 'etf', 'plan'].indexOf(positionOrDerivativePositionType) > -1"
              cols="12"
              md="12"
              class="pt-2"
            >
              <v-text-field
                v-model="myPosition.manager"
                :label="$t('views.stocks.stockModal.manager')"
                :disabled="loading"
                hide-details
                maxLength="100"
              />
            </v-col>

            <v-col
              v-if="positionOrDerivativePositionType !== 'cash'"
              cols="12"
              :md="showSector ? 7 : 12"
              class="pt-2"
            >
              <v-text-field
                v-model="myPosition.name"
                :label="$t('views.stocks.stockModal.name')"
                :disabled="loading"
                hide-details
                maxLength="100"
              />
            </v-col>
            <v-col v-if="showSector" cols="12" md="5" class="pt-2">
              <SectorBox
                v-model="myPosition.sector"
                group="position"
                :label="$t('views.stocks.stockModal.sector')"
                :disabled="loading"
              />
            </v-col>

            <v-col cols="12" md="12" class="pb-0">
              <TagBox
                v-model="myPosition.tags"
                group="position"
                :disabled="loading"
                :label="$t('views.stocks.stockModal.tags.label')"
                :hint="$t('views.stocks.stockModal.tags.hint')"
              />
            </v-col>

            <v-col v-if="showCountrySelector" cols="12" md="12" class="pt-0">
              <div class="items-flex">
                <CountrySelector
                  v-model="myPosition.exposureCountry"
                  :countries="allCountries"
                  :label="$t('views.stocks.stockModal.exposureCountry.label')"
                  :disabled="loading"
                  hide-details
                />
                <span class="ml-2">
                  <InfoIcon :size="15" :text="$t(`views.stocks.stockModal.exposureCountry.help`)" />
                </span>
              </div>
            </v-col>

            <v-col
              v-if="
                ['stock', 'etf', 'right', 'plan', 'bond', 'putOption', 'callOption'].indexOf(
                  positionOrDerivativePositionType
                ) > -1
              "
              cols="12"
              md="12"
            >
              <div class="items-flex">
                <v-text-field
                  v-model="myPosition.isin"
                  :label="$t('views.stocks.stockModal.isin.label')"
                  :disabled="loading"
                  hide-details
                  maxLength="30"
                />
                <span class="ml-2">
                  <InfoIcon :size="15" :text="$t('views.stocks.stockModal.isin.help')" />
                </span>
              </div>
            </v-col>

            <v-col cols="12" md="12" class="pt-2">
              <v-text-field
                v-model="myPosition.notes"
                :label="$t(`views.stocks.stockModal.notes`)"
                :disabled="loading"
                hide-details
                maxLength="500"
              />
            </v-col>

            <template v-if="showCustomDefaultDividendOriginRetentionPercent">
              <v-col cols="12" md="12" class="my-0 py-0 items-flex">
                <v-switch
                  v-model="position.customDefaultDividendOriginRetentionPercent"
                  :disabled="loading"
                  hide-details
                >
                  <template v-slot:label>
                    <span class="mr-2">
                      {{
                        $t(
                          `views.stocks.stockModal.customDefaultDividendOriginRetentionPercent.switch`
                        )
                      }}
                    </span>
                    <InfoIcon
                      :text="
                        $t(
                          `views.stocks.stockModal.customDefaultDividendOriginRetentionPercent.info`
                        )
                      "
                    />
                  </template>
                </v-switch>
                <div v-if="position.customDefaultDividendOriginRetentionPercent" class="ml-4">
                  <CurrencyInput
                    v-model="position.defaultDividendOriginRetentionPercent"
                    :label="
                      $t(
                        'views.stocks.stockModal.customDefaultDividendOriginRetentionPercent.label'
                      )
                    "
                    currency="%"
                    :disabled="loading"
                  />
                </div>
              </v-col>
            </template>

            <template v-if="showCustomDefaultDividendReturnPercent">
              <v-col cols="12" md="12" class="mb-0 mt-3 py-0 items-flex">
                <v-switch
                  v-model="position.customDefaultDividendReturnPercent"
                  :disabled="loading"
                  hide-details
                >
                  <template v-slot:label>
                    <span class="mr-2">
                      {{ $t(`views.stocks.stockModal.customDefaultDividendReturnPercent.switch`) }}
                    </span>
                    <InfoIcon
                      :text="$t(`views.stocks.stockModal.customDefaultDividendReturnPercent.info`)"
                    />
                  </template>
                </v-switch>
                <div v-if="position.customDefaultDividendReturnPercent" class="ml-4">
                  <CurrencyInput
                    v-model="position.defaultDividendReturnPercent"
                    :label="$t('views.stocks.stockModal.customDefaultDividendReturnPercent.label')"
                    currency="%"
                    :disabled="loading"
                  />
                </div>
              </v-col>
            </template>

            <!-- Para custom, aparece arriba como obligatorio. Para cash no permitimos -->
            <template v-if="['cash', 'custom'].indexOf(positionOrDerivativePositionType) === -1">
              <v-col v-show="myPosition.currency" cols="12" md="12" class="my-3 py-0">
                <v-switch v-model="position.customMarket.active" :disabled="loading" hide-details>
                  <template v-slot:label>
                    <span class="mr-2">
                      {{ $t(`views.stocks.stockModal.customMarket.switch`) }}
                    </span>
                    <InfoIcon :text="$t(`views.stocks.stockModal.customMarket.info`)" />
                  </template>
                </v-switch>
              </v-col>
              <template v-if="position.customMarket.active">
                <v-col cols="12" md="4" class="mt-0 mb-6 py-0">
                  <CurrencyInput
                    v-model="myPosition.customMarket.eod.close"
                    :currency="myPosition.currency"
                    :label="$t('views.stocks.stockModal.customMarketEodClose.label')"
                    :disabled="loading"
                  />
                </v-col>
              </template>
            </template>

            <template
              v-if="positionOrDerivativePositionType === 'custom' || position.customMarket.active"
            >
              <v-col
                cols="12"
                md="12"
                class="py-0"
                :class="{ 'pt-5': positionOrDerivativePositionType === 'custom' }"
              >
                <PositionFormCustomMarketHistorical
                  v-model="myPosition.customMarket.eodHistorical"
                  :myPosition="myPosition"
                  :loading="loading"
                />
              </v-col>
            </template>
          </template>
        </template>
        <!-- <v-col
        v-if="['stock', 'fund', 'etf', 'plan', 'right', 'putOption', 'callOption'].indexOf(positionOrDerivativePositionType) > -1"
        cols="12"
        md="12"
      >
        <div class="items-flex">
          <v-select
            v-model="myPosition.category"
            :items="categorySelectorList"
            :label="$t('views.stocks.stockModal.category.label')"
            :disabled="loading"
            hide-details
          ></v-select>
          <span class="ml-2">
            <InfoIcon :size="15" :text="$t('views.stocks.stockModal.category.help')" />
          </span>
        </div>
      </v-col> -->
      </v-row>
    </template>
  </div>
</template>

<script>
import { get } from 'lodash'
import { getPositionTypes } from '@/lib/positions/positions.js'
import PositionFormMixin from '@/views/components/common/positions/PositionFormMixin'
import PositionSearch from '@/views/components/common/PositionSearch'
import PositionFormCustomMarketHistorical from '@/views/components/common/positions/PositionFormCustomMarketHistorical'
import CustomTypeBox from '@/views/components/common/CustomTypeBox'

export default {
  components: { PositionSearch, PositionFormCustomMarketHistorical, CustomTypeBox },
  mixins: [PositionFormMixin],
  data: () => ({
    type: 'position',
    getDataFromWatchActive: false,
  }),
  computed: {
    positionPositionTypes() {
      return getPositionTypes({ model: 'position' })
    },
  },
  methods: {
    getDataFromWatch(item) {
      this.getDataFromPositionActive = false
      if (item) {
        this.$set(this.myPosition, 'type', get(item, 'type', undefined))
        this.$set(this.myPosition, 'ticker', get(item, 'ticker', undefined))

        // time out to skip watchers (same in ImportModalRequiresDictCard and WatchModal)

        setTimeout(() => {
          this.$set(this.myPosition, 'country', get(item, 'country', undefined))
        }, 100)

        setTimeout(() => {
          this.$set(this.myPosition, 'exchange', get(item, 'exchange', undefined))
          this.$set(this.myPosition, 'currency', get(item, 'currency', undefined))
          this.$set(this.myPosition, 'name', get(item, 'name', undefined))
          this.$set(this.myPosition, 'sector', get(item, 'sector', undefined))
          this.$set(this.myPosition, 'manager', get(item, 'manager', undefined))
          this.$set(this.myPosition, 'tags', get(item, 'tags', undefined))
        }, 300)
      }
    },
  },
}
</script>

<style lang="scss">
#position-form {
  h1 {
    font-size: 16px;
  }
  .items-flex {
    display: flex;
    align-items: flex-end;
  }
  .position-form-link {
    font-size: 13px;
  }
  .position-form-select-data-from-watch a {
    font-size: 14px;
  }
}
</style>
